import { Children, Suspense, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { PublicPrerenderContext } from "@/lib/publicPrerender";

type PendingSection = { ready: boolean; promise: Promise<void>; release: () => void };
const sections = new Map<string, PendingSection>();
let homeMounted = false;
let publicMounted = false;
const initialPublicPath = typeof document !== "undefined"
  && document.getElementById("root")?.dataset.publicPrerender === "true"
  && !location.hash ? location.pathname : null;
const initialHome = typeof document !== "undefined"
  && location.pathname.replace(/\/$/, "") === import.meta.env.BASE_URL.replace(/\/$/, "")
  && !location.hash
  && document.getElementById("root")?.dataset.publicPrerender === "true";

function pendingSection(id: string) {
  let section = sections.get(id);
  if (!section) {
    let resolve!: () => void;
    const promise = new Promise<void>((done) => { resolve = done; });
    section = { ready: false, promise, release: () => { section!.ready = true; resolve(); } };
    sections.set(id, section);
  }
  return section;
}

export function useInitialHomeDeferral() {
  const prerender = useContext(PublicPrerenderContext);
  // Authenticated sessions and client navigations retain normal rendering.
  const [enabled] = useState(() => !prerender && !!initialHome && !homeMounted
    && !/\b__(?:session|client_uat|clerk)[^=;]*=/.test(document.cookie));
  useEffect(() => { homeMounted = true; }, []);
  return enabled;
}

/** Inner-page equivalent: no placeholders, height estimates or client-nav delay. */
export function useInitialPublicDeferral() {
  const prerender = useContext(PublicPrerenderContext);
  const [enabled] = useState(() => !prerender && !!initialPublicPath && !publicMounted
    && location.pathname === initialPublicPath
    && !/\b__(?:session|client_uat|clerk)[^=;]*=/.test(document.cookie));
  useEffect(() => { publicMounted = true; }, []);
  return enabled && location.pathname === initialPublicPath;
}

function Ready({ enabled, id, children }: { enabled: boolean; id: string; children: ReactNode }) {
  if (enabled) {
    const section = pendingSection(id);
    if (!section.ready) throw section.promise;
  }
  return children;
}

/** Hydrate only the actual section in view, not every sibling further down. */
export function DeferredPublicSections({ enabled, id, children }: {
  enabled: boolean; id: string; children: ReactNode;
}) {
  return Children.toArray(children).map((child, index) => (
    <DeferredPublicSection key={`${id}-${index}`} enabled={enabled} id={`${id}-${index}`}>
      {child}
    </DeferredPublicSection>
  ));
}

/** Suspend hydration, not HTML: complete server markup stays in its exact place. */
export default function DeferredPublicSection({
  enabled, id, children,
}: { enabled: boolean; id: string; children: ReactNode }) {
  const marker = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!enabled) {
      marker.current?.setAttribute("data-activation", "ready");
      return;
    }
    const section = pendingSection(id);
    marker.current?.setAttribute("data-activation", section.ready ? "ready" : "pending");
    const activate = () => {
      section.release();
      marker.current?.setAttribute("data-activation", "ready");
    };
    const targets: Element[] = [];
    let depth = 0;
    for (let node = marker.current?.nextSibling; node; node = node.nextSibling) {
      if (node.nodeType === Node.COMMENT_NODE) {
        if (/^\$(?:[?!])?$/.test(node.nodeValue ?? "")) depth++;
        else if (node.nodeValue === "/$" && --depth === 0) break;
      } else if (node instanceof Element && depth > 0) targets.push(node);
    }
    if (!targets.length || !("IntersectionObserver" in window)) {
      activate();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        activate();
      }
    }, { rootMargin: "0px" });
    targets.forEach((target) => observer.observe(target));
    return () => { observer.disconnect(); section.release(); };
  }, [enabled, id]);
  return <>
    <span hidden aria-hidden="true" data-deferred-section={id} ref={marker} />
    <Suspense fallback={null}><Ready enabled={enabled} id={id}>{children}</Ready></Suspense>
  </>;
}
